import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplicationProperties } from "../../../../../../../hooks/useApplicationProperties"
import { digitalManagerApi } from "../../../../../../../services/api"
import { FieldRow, FormPage } from "../../../../../../components/FormPage"
import PropertyName from "../../../../../../components/forms/PropertyName"
import PropertyValue from "../../../../../../components/forms/PropertyValue"
import {
  ApplicationPropertyDetail,
  ApplicationPropertyGet,
  ApplicationPropertyPossibleValue,
  ApplicationPropertyTypeEnum,
  ApplicationPropertyValPostRole,
} from "../../../../../../models/ApplicationProperty"
import { ApplicationRolePermissionDetailGet } from "../../../../../../models/ApplicationRolePermission"

const validationSchema = z.object({
  propertyName: z.string().min(1, "Property name is required"),
  propertyValue: z.string().min(1, "Property value is required"),
  roleId: z.string(),
  permissionId: z.string(),
})

export function ApplicationRolePermissionPropertyCreate() {
  const { applicationId, roleId, permissionId } = useParams()
  const { applicationProperties, isLoadingApplicationProperties } =
    useApplicationProperties(applicationId, false, true)
  const [
    applicationRolePermissionPropertiesIds,
    setApplicationRolePermissionPropertiesIds,
  ] = useState<ApplicationPropertyDetail[]>([])

  const navigate = useNavigate()
  const returnURL = `/applications/${applicationId}/roles/${roleId}/permissions/${permissionId}#properties`

  const [propertyType, setPropertyType] = useState<ApplicationPropertyTypeEnum>(
    ApplicationPropertyTypeEnum.String
  )
  const [propertyPossibleValue, setPropertyPossibleValue] =
    useState<ApplicationPropertyPossibleValue>({ value: "", label: "" })

  const propertyPossibleValues: { [key: string]: ApplicationPropertyPossibleValue[] } =
    useMemo(() => {
      const propertyPossibleValuesDict: {
        [key: string]: ApplicationPropertyPossibleValue[]
      } = {}
      if (applicationProperties?.length) {
        applicationProperties.forEach((property: ApplicationPropertyGet) => {
          propertyPossibleValuesDict[property.type] = property.possibleValues
        })
      }
      return propertyPossibleValuesDict
    }, [applicationProperties])

  useEffect(() => {
    if (applicationId && roleId && permissionId) {
      digitalManagerApi
        .get<ApplicationRolePermissionDetailGet>(
          `/applications/${applicationId}/roles/${roleId}/permissions/${permissionId}`
        )
        .then((res) => {
          if (res.status === StatusCodes.OK && res.data?.properties.length) {
            const filteredProperties = res.data.properties.map(
              (property: ApplicationPropertyDetail) => ({
                name: property.name,
                value: property.value,
              })
            )
            setApplicationRolePermissionPropertiesIds(filteredProperties)
          }
        })
        .catch((err) => {
          if (err.response.status) {
            navigate(returnURL)
          }
        })
    }
  }, [applicationId, navigate, permissionId, returnURL, roleId])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
    setValue,
  } = useForm<{ propertyName: string } & ApplicationPropertyValPostRole>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      propertyName: "",
      propertyValue: "",
      roleId,
      permissionId,
    })
    setPropertyPossibleValue({ value: "", label: "" })
  }, [roleId, permissionId, reset])

  const onSubmit = useCallback(
    async (values: { propertyName: string } & ApplicationPropertyValPostRole) => {
      try {
        const response = await digitalManagerApi.post(
          `/applications/${applicationId}/properties/${values.propertyName}/values`,
          {
            propertyValue: values.propertyValue,
            roleId: values.roleId,
            permissionId: values.permissionId,
          }
        )
        if (response.status === StatusCodes.CREATED) {
          toast.success("Property added to role's permission")
          navigate(returnURL)
        }
      } catch {
        toast.error("Cannot add property to role's permission")
      }
    },
    [applicationId, navigate, returnURL]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Property Name">
        <>
          <PropertyName<{ propertyName: string } & ApplicationPropertyValPostRole>
            control={control}
            setValue={setValue}
            applicationProperties={applicationProperties}
            setPropertyType={setPropertyType}
            setPropertyPossibleValue={setPropertyPossibleValue}
            isLoadingApplicationProperties={isLoadingApplicationProperties}
            errors={errors}
          />
          {errors.propertyName && (
            <MUITextField
              isDisabled
              value=""
              errors={{
                propertyName: {
                  message:
                    typeof errors.propertyName?.message === "string"
                      ? errors.propertyName.message
                      : "",
                },
              }}
            />
          )}
        </>
      </FieldRow>
      <FieldRow label="Property Value">
        <PropertyValue<{ propertyName: string } & ApplicationPropertyValPostRole>
          control={control}
          setValue={setValue}
          applicationRolePermissionPropertiesIds={applicationRolePermissionPropertiesIds}
          propertyPossibleValues={propertyPossibleValues}
          propertyType={propertyType}
          propertyPossibleValue={propertyPossibleValue}
          setPropertyPossibleValue={setPropertyPossibleValue}
          errors={errors}
        />
      </FieldRow>
    </FormPage>
  )
}
